import {getFunctions, httpsCallable} from 'firebase/functions'
import {signInWithCustomToken, reauthenticateWithCredential, EmailAuthProvider, signOut} from 'firebase/auth'
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser'
import {collection, getDocs, query, where} from 'firebase/firestore'
import {auth, app, db} from '../../../firebase'

// Client for the passkey (WebAuthn) flow. Additive to email+password — the
// backend (functions/src/functions/callable/passkeys.ts) verifies the assertion
// and mints a Firebase custom token; we exchange it for a normal session.

const fns = () => getFunctions(app, 'us-central1')

export function passkeysSupported(): boolean {
  try {
    return browserSupportsWebAuthn()
  } catch {
    return false
  }
}

/** WebAuthn user-cancel / no-credential — treat as a silent no-op, not an error. */
export function isPasskeyCancel(err: any): boolean {
  const name = err?.name || ''
  return name === 'NotAllowedError' || name === 'AbortError'
}

function defaultDeviceName(): string {
  const ua = navigator.userAgent || ''
  const os = /iPhone|iPad/i.test(ua) ? 'iPhone / iPad'
    : /Android/i.test(ua) ? 'Android'
    : /Macintosh|Mac OS/i.test(ua) ? 'Mac'
    : /Windows/i.test(ua) ? 'Windows'
    : 'Device'
  return `${os} · ${new Date().toLocaleDateString()}`
}

// ── Re-authenticate with the password (required before enrolling a passkey) ──
// Enrolling a new sign-in factor is sensitive, so the backend requires a RECENT
// auth (fresh auth_time). We reauth with the password, then force-refresh the ID
// token so the enrol callable sees the new auth_time.
export async function reauthWithPassword(password: string): Promise<void> {
  const user = auth.currentUser
  if (!user || !user.email) throw new Error('You need to be signed in.')
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password))
  await user.getIdToken(true)
}

// ── Revoke every session ("sign out of all devices") ─────────────────────────
// Server revokes the caller's refresh tokens; we then sign out locally so the
// current session ends immediately too.
export async function revokeAllSessions(): Promise<void> {
  const call = httpsCallable<unknown, {ok: boolean}>(fns(), 'revokeMySessions')
  await call({})
  await signOut(auth)
}

// ── Enrol the current device (must be signed in + recently re-authenticated) ──
export async function enrollPasskey(deviceName?: string): Promise<void> {
  const begin = httpsCallable<{origin: string}, {options: any}>(fns(), 'passkeyRegisterBegin')
  const {data} = await begin({origin: window.location.origin})
  const attResp = await startRegistration({optionsJSON: data.options})
  const verify = httpsCallable<{response: any; deviceName?: string}, {ok: boolean}>(fns(), 'passkeyRegisterVerify')
  await verify({response: attResp, deviceName: deviceName || defaultDeviceName()})
}

// ── Sign in with a passkey (usernameless) ────────────────────────────────────
export async function signInWithPasskey(): Promise<void> {
  const begin = httpsCallable<{origin: string}, {options: any; challengeId: string}>(fns(), 'passkeyAuthBegin')
  const {data} = await begin({origin: window.location.origin})
  const authResp = await startAuthentication({optionsJSON: data.options})
  const verify = httpsCallable<{response: any; challengeId: string}, {token: string}>(fns(), 'passkeyAuthVerify')
  const {data: res} = await verify({response: authResp, challengeId: data.challengeId})
  if (!res?.token) throw new Error('Passkey sign-in failed.')
  const cred = await signInWithCustomToken(auth, res.token)
  await completeSession(cred.user)
}

export async function listPasskeys(): Promise<
  Array<{credentialId: string; deviceName: string; createdAt: number | null; lastUsedAt: number | null}>
> {
  const call = httpsCallable<unknown, {passkeys: any[]}>(fns(), 'passkeyList')
  const {data} = await call({})
  return (data?.passkeys as any[]) || []
}

export async function removePasskey(credentialId: string): Promise<void> {
  const call = httpsCallable<{credentialId: string}, {ok: boolean}>(fns(), 'passkeyRemove')
  await call({credentialId})
}

// Mirrors the email/password post-auth (localStorage + preferred-homepage
// redirect) so a passkey session behaves identically to a password one.
async function completeSession(user: any): Promise<void> {
  const useruid = user.uid
  localStorage.setItem('user_uid', useruid)

  const idTokenResult = await user.getIdTokenResult()
  localStorage.setItem('role', idTokenResult.claims.isAdmin ? 'admin' : 'user')

  const expirationTime = new Date().getTime() + 1000 * 60 * 2
  const loginTime = new Date().getTime()

  const docs = await getDocs(query(collection(db, 'users'), where('uid', '==', useruid)))
  if (docs.docs.length > 0) {
    const d: any = docs.docs[0].data()
    if (Array.isArray(d.roles) && d.roles.includes('admin')) localStorage.setItem('role', 'admin')
    localStorage.setItem('user', JSON.stringify({...d, expiresIn: expirationTime, loginTime}))
    window.location.href = d.defaultHomepage || '/dashboard'
  } else {
    localStorage.setItem('user', JSON.stringify({uid: useruid, expiresIn: expirationTime, loginTime}))
    window.location.href = '/dashboard'
  }
}
